<template>
    <TransitionRoot
        as="template"
        :show="isOpen"
    >
        <component
            :is="drawerWrapper"
            :class="drawerWrapperClass"
            v-on="drawerWrapperListeners"
        >
            <TransitionChild
                v-if="hasOverlay"
                as="template"
                enter="ease-in-out duration-500"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-500"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-backdrop transition-opacity"
                    data-spec-class="drawer__overlay"
                    @click="handleClose"
                />
            </TransitionChild>

            <div class="pointer-events-none fixed inset-0 z-20 overflow-hidden">
                <div class="pointer-events-none absolute inset-0 overflow-hidden">
                    <div
                        class="pointer-events-none fixed inset-y-0 right-0 flex w-full justify-end pl-14 sm:pl-40"
                    >
                        <TransitionChild
                            as="template"
                            enter="transform transition ease-in-out duration-500 sm:duration-700"
                            enter-from="translate-x-full"
                            enter-to="translate-x-0"
                            leave="transform transition ease-in-out duration-500 sm:duration-700"
                            leave-from="translate-x-0"
                            leave-to="translate-x-full"
                        >
                            <component
                                :is="panelComponent"
                                class="pointer-events-none relative h-full w-full"
                            >
                                <div
                                    :class="panelClass"
                                    data-spec-class="drawer__panel"
                                >
                                    <TransitionChild
                                        as="template"
                                        enter="ease-in-out duration-500"
                                        enter-from="opacity-0"
                                        enter-to="opacity-100"
                                        leave="ease-in-out duration-500"
                                        leave-from="opacity-100"
                                        leave-to="opacity-0"
                                    >
                                        <div
                                            class="absolute top-0 left-0 z-10 -ml-12 flex w-10 flex-col items-end gap-2 pt-4"
                                        >
                                            <div
                                                class="flex size-10 cursor-pointer items-center justify-center rounded-full bg-surface--elevated shadow-lg transition duration-500 ease-in-out hover:bg-gray-150 dark:hover:bg-gray-600"
                                                @click="handleClose"
                                            >
                                                <BaseButtonClose
                                                    data-spec-class="drawer__close-button"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                class="hidden h-10 cursor-pointer items-center rounded-full bg-surface--elevated pl-3 shadow-lg transition duration-500 ease-in-out hover:bg-gray-150 sm:flex dark:hover:bg-gray-600"
                                                data-spec-class="drawer__fullscreen-button"
                                                @click="toggleFullscreen"
                                            >
                                                <span
                                                    class="text-sm whitespace-nowrap text-tertiary"
                                                    data-spec-class="drawer__fullscreen-label"
                                                >
                                                    {{ fullscreenLabel }}
                                                </span>
                                                <span
                                                    class="flex size-10 shrink-0 items-center justify-center"
                                                >
                                                    <ArrowsPointingInIcon
                                                        v-if="isFullscreen"
                                                        class="size-5 text-tertiary"
                                                        aria-hidden="true"
                                                    />
                                                    <ArrowsPointingOutIcon
                                                        v-else
                                                        class="size-5 text-tertiary"
                                                        aria-hidden="true"
                                                    />
                                                </span>
                                            </button>
                                        </div>
                                    </TransitionChild>

                                    <div
                                        class="relative flex h-full flex-col divide-y divide-divider bg-surface shadow-xl dark:shadow-2xl"
                                    >
                                        <div
                                            class="flex min-h-0 flex-1 flex-col overflow-y-auto py-6"
                                        >
                                            <div class="px-4 sm:px-6">
                                                <div class="flex items-start justify-between">
                                                    <component
                                                        :is="titleComponent"
                                                        class="text-base font-semibold text-primary"
                                                    >
                                                        {{ title }}
                                                    </component>
                                                    <div class="ml-3 flex h-7 items-center gap-2">
                                                        <slot name="header-right" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                v-if="shouldRenderContent"
                                                class="relative mt-6 flex-1 px-4 sm:px-6"
                                            >
                                                <slot />
                                            </div>
                                        </div>
                                        <div
                                            v-if="$slots.footer"
                                            class="flex shrink-0 justify-end px-4 py-4"
                                        >
                                            <slot name="footer" />
                                        </div>
                                    </div>
                                </div>
                            </component>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </component>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/vue/24/outline';
import { useCore } from '@/modules/your/composables/core';
import { SizeAll } from '@/types';
import BaseButtonClose from './BaseButtonClose.vue';

interface Props {
    modelValue?: boolean;

    title?: string;
    showsOverlay?: boolean;
    drawerGroup?: string; // Groups drawers to ensure only one per group is open at a time
    panelWidth?: SizeAll;
}

interface Emits {
    (e: 'update:modelValue', value: boolean): void;
    (e: 'close'): void;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    title: '',
    showsOverlay: false,
    drawerGroup: 'default',
    panelWidth: SizeAll.Normal,
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const TRANSITION_DURATION_MS = 700;

const isOpen = defineModel<boolean>({ default: false });
const instanceId = useId();
const { openDrawer, closeDrawer, getActiveDrawerInGroup } = useCore();
const shouldRenderContent = ref(false);
const isFullscreen = ref(false);
let hideContentTimeout: ReturnType<typeof setTimeout> | null = null;

const hasOverlay = computed(() => props.showsOverlay || isFullscreen.value);

const drawerWrapper = computed(() => (props.showsOverlay ? Dialog : 'div'));
const drawerWrapperClass = computed(() => {
    if (props.showsOverlay) {
        return 'relative z-[100]';
    }

    return isFullscreen.value
        ? 'fixed inset-0 z-[100] overflow-hidden'
        : 'pointer-events-none fixed inset-0 z-20 overflow-hidden';
});
const panelComponent = computed(() => (props.showsOverlay ? DialogPanel : 'div'));
const titleComponent = computed(() => (props.showsOverlay ? DialogTitle : 'h2'));

const drawerWrapperListeners = computed(() => {
    if (!props.showsOverlay) return {};
    return { close: handleClose };
});

const panelClass = computed(() => ({
    'pointer-events-auto relative ml-auto h-full w-full transition-[max-width] duration-500 ease-in-out': true,
    'max-w-md': !isFullscreen.value && props.panelWidth === SizeAll.Normal,
    'max-w-2xl': !isFullscreen.value && props.panelWidth === SizeAll.Large,
    'max-w-full': isFullscreen.value,
}));

const fullscreenLabel = computed(() =>
    isFullscreen.value
        ? t('baseComponents.baseDrawer.minimize')
        : t('baseComponents.baseDrawer.maximize')
);

const clearHideContentTimeout = () => {
    if (hideContentTimeout) {
        clearTimeout(hideContentTimeout);
        hideContentTimeout = null;
    }
};

// Delays rendering slot content until after the transition starts, preventing the slide-in
// animation from being skipped. Without this delay, Vue renders the slot immediately which
// causes the TransitionChild to miss the initial state, resulting in no animation.
const showContent = async () => {
    clearHideContentTimeout();
    await nextTick();
    requestAnimationFrame(() => {
        shouldRenderContent.value = true;
    });
};

// Delays hiding slot content until after the transition completes, ensuring the content
// remains visible during the slide-out animation. If hidden immediately, the content would
// disappear before the drawer finishes sliding out, creating a jarring user experience.
const hideContent = () => {
    hideContentTimeout = setTimeout(() => {
        shouldRenderContent.value = false;
        hideContentTimeout = null;
    }, TRANSITION_DURATION_MS);
};

const handleDrawerOpen = () => {
    openDrawer(props.drawerGroup, instanceId);
    showContent();
};

const handleDrawerClose = () => {
    const activeDrawer = getActiveDrawerInGroup(props.drawerGroup);
    if (activeDrawer === instanceId) {
        closeDrawer(props.drawerGroup);
    }
    isFullscreen.value = false;
    hideContent();
};

const handleClose = () => {
    isOpen.value = false;
    emit('close');
};

const toggleFullscreen = () => {
    isFullscreen.value = !isFullscreen.value;
};

const handleKeydown = (event: KeyboardEvent) => {
    if (props.showsOverlay) return;
    if (event.key !== 'Escape') return;
    if (!isOpen.value) return;

    const activeDrawer = getActiveDrawerInGroup(props.drawerGroup);
    if (activeDrawer !== instanceId) return;

    handleClose();
};

watch(
    isOpen,
    (newValue) => {
        if (newValue) {
            handleDrawerOpen();
        } else {
            handleDrawerClose();
        }
    },
    { immediate: true }
);

watch(
    () => getActiveDrawerInGroup(props.drawerGroup),
    (activeDrawerId) => {
        if (activeDrawerId && activeDrawerId !== instanceId && isOpen.value) {
            isOpen.value = false;
        }
    }
);

watch(
    isOpen,
    (newValue) => {
        if (props.showsOverlay) return;

        if (newValue) {
            window.addEventListener('keydown', handleKeydown);
        } else {
            window.removeEventListener('keydown', handleKeydown);
        }
    },
    { immediate: true }
);

onUnmounted(() => {
    clearHideContentTimeout();
    window.removeEventListener('keydown', handleKeydown);
});
</script>
